// ============================================================================
// JARVISTRAVEL - PAYWALL (J5)
// The gate a Trip Pass user hits when starting another trip: buy a new Trip
// Pass, or upgrade to Explore, while their journals stay free to view. Plans
// + checkout come from the (model-ready) payments seam.
// Stripe, via the backend payments seam (JAR-657). Clerk is auth only.
// ============================================================================

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Sparkles, BookOpen, ArrowRight } from 'lucide-react';
import { Modal } from '@/app/components/shared';
import {
  listPlans,
  redirectToCheckout,
  planTitle,
  annualSavingFor,
  getEntitlement,
  entitlementChanged,
  readEntitlementBeforeCheckout,
  forgetEntitlementBeforeCheckout,
  type SubscriptionPlan,
} from '@/app/api/payments';
import { isDemoSession } from '@/app/api/client';
import { MARKETING_URLS } from '@/app/constants';
import { PlanPrice } from './PlanPrice';

interface PaywallProps {
  isOpen: boolean;
  onClose: () => void;
  /** Why a plan is needed here. Unset, it's a Trip Pass holder starting another trip. */
  title?: string;
  intro?: ReactNode;
}

export function Paywall({ isOpen, onClose, title = 'Plan another trip', intro }: PaywallProps) {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  // Derived from the plans on screen, so the badge can never contradict the
  // prices beside it. Null when the catalogue has one cadence: no claim made.
  const [busy, setBusy] = useState<string | null>(null);
  const [demoNote, setDemoNote] = useState(false);
  const [checkoutFailed, setCheckoutFailed] = useState(false);
  const [purchasePending, setPurchasePending] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoadError(false);
    listPlans().then(setPlans).catch(() => { setPlans([]); setLoadError(true); });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    // Reset transient state so it doesn't linger on re-open.
    setDemoNote(false);
    setCheckoutFailed(false);
    setPurchasePending(false);
    setBusy(null);
    load();
    // A checkout started in this tab whose purchase hasn't been seen to land:
    // the plan can take a moment to arrive after Stripe sends them back, and
    // buying again in that window buys twice. So it's said before the plans.
    const before = readEntitlementBeforeCheckout();
    if (!before) return;
    let live = true;
    void getEntitlement().then((now) => {
      if (!live) return;
      if (entitlementChanged(before, now)) forgetEntitlementBeforeCheckout();
      else setPurchasePending(true);
    });
    return () => {
      live = false;
    };
  }, [isOpen, load]);

  const choose = async (plan: SubscriptionPlan) => {
    setBusy(plan.id);
    setCheckoutFailed(false);
    try {
      const redirected = await redirectToCheckout(plan.id);
      // No checkout url: the demo has no billing backend, and anywhere else
      // checkout didn't open.
      if (!redirected) {
        if (isDemoSession()) setDemoNote(true);
        else setCheckoutFailed(true);
      }
    } catch {
      setCheckoutFailed(true);
    } finally {
      setBusy(null);
    }
  };

  const sorted = useMemo(() => [...plans].sort((a, b) => a.sort_order - b.sort_order), [plans]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <div className="space-y-4">
        <p className="text-sm text-body">
          {intro ?? <>You’re on a <span className="font-semibold text-ink">Trip Pass</span>: one trip at a time. Start a new one with another pass, or go unlimited with Explore.</>}
        </p>

        {purchasePending && (
          <p role="status" className="text-sm text-body bg-surface-raised rounded-xl px-3 py-2">
            A purchase you started here hasn’t been confirmed yet. Give it a minute before buying again, so you aren’t charged twice.
          </p>
        )}

        {/* grid-cols-1 holds the column to the screen (gridColumns.test.ts). */}
        <div className="grid grid-cols-1 gap-3">
          {sorted.map((plan) => {
            const sub = plan.product_type === 'subscription';
            const savingPct = annualSavingFor(plan, plans);
            return (
              <button
                key={plan.id}
                onClick={() => choose(plan)}
                disabled={!!busy}
                className={`text-left rounded-2xl border p-4 transition-colors disabled:opacity-60 ${sub ? 'border-accent bg-accent/5 hover:bg-accent/10' : 'border-border bg-surface-raised hover:bg-border/30'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-ink">
                    {sub && <Sparkles className="w-4 h-4 text-accent" strokeWidth={2} aria-hidden="true" />}
                    {planTitle(plan)}
                  </span>
                  {savingPct !== null && <span className="text-[10px] font-bold uppercase tracking-wide text-accent bg-accent/10 rounded-md px-2 py-0.5">Save {savingPct}%</span>}
                </div>
                <PlanPrice plan={plan} leadClassName="text-lg font-bold text-ink tabular-nums" />
                {plan.description && <p className="text-sm text-muted mt-0.5">{plan.description}</p>}
                <span className="inline-flex items-center gap-1 text-xs font-medium text-accent mt-2">
                  {busy === plan.id ? 'Opening checkout…' : sub ? 'Go unlimited' : 'Buy a Trip Pass'} <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
                </span>
              </button>
            );
          })}
        </div>

        {loadError && sorted.length === 0 && (
          <div className="text-sm text-muted bg-surface-raised rounded-xl px-3 py-3 text-center">
            We couldn’t load plans right now.{' '}
            <button onClick={load} className="font-medium text-accent hover:opacity-80">Try again</button>
          </div>
        )}

        {demoNote && (
          <p className="text-xs text-muted bg-surface-raised rounded-xl px-3 py-2">Checkout will open once a payment method is set up.</p>
        )}

        {checkoutFailed && (
          <p role="alert" className="text-sm text-danger bg-danger/10 rounded-xl px-3 py-2">
            We couldn’t open checkout. Nothing was charged. Try again in a moment.
          </p>
        )}

        <div className="flex items-start gap-2 text-xs text-muted border-t border-border pt-3">
          <BookOpen className="w-4 h-4 mt-0.5 text-accent shrink-0" strokeWidth={2} aria-hidden="true" />
          Your journals stay yours. They're free to view, even between trips.
        </div>
        <p className="text-xs text-muted text-center">
          By continuing, you agree to our{' '}
          <a href={MARKETING_URLS.terms} target="_blank" rel="noreferrer" className="text-accent hover:underline">Terms of Service</a>{' '}
          and{' '}
          <a href={MARKETING_URLS.privacy} target="_blank" rel="noreferrer" className="text-accent hover:underline">Privacy Policy</a>.
        </p>
      </div>
    </Modal>
  );
}

export default Paywall;
